import { useCallback, useEffect, useRef, useState, type FocusEvent, type MouseEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Bot,
  Briefcase,
  ChevronsLeft,
  ChevronsRight,
  GraduationCap,
  Home,
  Images,
  LifeBuoy,
  Megaphone,
  Presentation,
  Share2,
  Star,
  type LucideIcon,
  Lightbulb,
  MessagesSquare,
} from 'lucide-react';
import {
  PORTAL_NAV_SOON_HEADING,
  canSeeReports,
  isLiveHeader,
  isPortalHomeActive,
  isPortalNavItemActive,
  navigationNavItems,
  isPortalReportRowActive,
  liveNavItems,
  portalReportLinks,
  portalReportRowKey,
  portalReportRows,
  navSectionStartsOpen,
  soonNavItems,
  type PortalNavItemId,
} from '../../../lib/portalNav';
import type { PortalNavSelection } from '../../../lib/portalReport/locationSelection';
import { collapseApplies, collapseLabel, flyoutPosition, tipPosition, type FlyoutEntry } from '../../../lib/portalNavRail';
import { fetchPortalMe } from './portalApi';
import { usePortalViewerNav } from './usePortalViewerNav';

// CLIENT PORTAL — the left nav.
//
// ⚠️⚠️ IT READS THE FULL SCOPE FROM `/api/portal/me`, NOT THE REPORT PAYLOAD.
// A report's `accounts` is POST-NARROWING — drilling into one club reduces it to
// that club — so a nav built from it would collapse to a single row the moment a
// client used it, and there would be no way back to the others. `/me` is the
// scope as resolved, and it does not move.
//
// ⚠️ NO EXTRA REQUEST: it shares `/me`'s react-query key with `PortalRoutes` and
// `PortalHome`, so all three read one cached response. `PortalShell` only asks
// for a nav on pages that already have a session, and a 403 or a pending fetch
// renders NOTHING rather than an empty frame.
//
// ⚠️⚠️ THE COMING-SOON ITEMS ARE SPANS, NOT LINKS, and that is the requirement
// rather than a styling choice: they must be genuinely non-interactive — not
// clickable-then-disappointing, and not reachable by keyboard as though they led
// somewhere. A `<span>` has no href, is not in the tab order, and carries
// `aria-disabled` so a screen reader is told what a sighted reader is. The model
// makes it structural: a `status: 'soon'` item HAS no `path` field, so there is
// nothing to render as an anchor even by accident.
//
// ⚠️ ONE MUTED HEADING OVER THE GROUP, and no per-row badge. Five "coming soon"
// pills shouting at once reads as an unfinished product; said once, quietly, it
// reads as a roadmap.
//
// ⚠️ "Asset management", NOT "Club assets" — see src/lib/portalNav.ts. A
// dealership is not a club, and this product has two verticals.
//
// ⚠️⚠️ AND `.psh-sublist` NOW HAS TWO KINDS OF CHILD (§6, 2026-09-03): the
// report's per-location LINKS and the coming-soon sections' SPANS. The
// coming-soon styling therefore has to apply at CHILD level rather than to the
// list — see the `.psh-sublist span` rule in index.css, which exists because
// `.psh-sublist a` alone would leave a sub-item styled as a live link.

// ⚠️⚠️ BELOW 900px IT IS A BURGER (§6, 2026-09-04), AND WE MADE THAT NECESSARY.
//
// Below 900px the rail is nothing at all and the nav is a WRAPPING STRIP under
// the header. That was fine at six parents plus club rows. Round 6 §6 added
// fourteen coming-soon rows, so the strip became roughly twenty wrapping chips
// above the content — literally "a bunch of words at the top", which is what
// Scott reported. The desktop verification could not see it: the rail only
// exists above 900px.
//
// ⚠️⚠️ CLOSED IS A CLASS, NEVER AN INLINE STYLE. `style={{display:'none'}}`
// beats any stylesheet rule short of `!important`, so the DESKTOP nav would
// vanish whenever this happened to be closed — and it starts closed. The
// visibility rules are MUTUALLY EXCLUSIVE by media query rather than by
// specificity or source order, which this stylesheet has lost three times
// (see "A SELECTOR THAT READS CORRECTLY AND SILENTLY LOSES" in CLAUDE.md).

// ⚠️ ONE ICON PER MAIN HEADING, coming-soon ones included so the group does not
// read as ragged (Run J). lucide-react is already in the portal bundle — the
// KyckSocial pages import it — so icons are free. They are DECORATIVE: the label
// is the accessible name, hence `aria-hidden`. Children carry none (the hierarchy
// is size, not decoration). Keyed by id and typed as a total Record, so a new
// section is a compile error until it has an icon.
const NAV_ICON: Record<PortalNavItemId, LucideIcon> = {
  home: Home,
  reports: BarChart3,
  strategy: Presentation,
  college: GraduationCap,
  inspiration: Lightbulb,
  kyckConnect: MessagesSquare,
  assets: Images,
  campaigns: Megaphone,
  organicSocial: Share2,
  reviews: Star,
  bots: Bot,
  services: Briefcase,
  support: LifeBuoy,
};

function NavIcon({ id }: { id: PortalNavItemId }) {
  const Icon = NAV_ICON[id];
  return <Icon className="psh-navicon" aria-hidden="true" focusable="false" />;
}

/**
 * The open/closed control for one section (§5, 2026-09-04).
 *
 * ⚠️⚠️ THE DISCLOSURE IS THE FOCUSABLE; THE LABEL STAYS A `<span>`. A
 * coming-soon parent now carries TWO behaviours in one row — a non-interactive
 * name and an interactive control — and the obvious shortcut is to make the label
 * a button to get a click target. That would undo round 6, which made
 * pathlessness STRUCTURAL so a coming-soon item is genuinely not a destination,
 * and round 7, which measured only 5 focusables in a 20-row nav. Turning fourteen
 * labels into buttons puts nineteen tab stops in front of a client for sections
 * that do not exist yet.
 *
 * ⚠️ ITS ACCESSIBLE NAME CARRIES THE SECTION, because `aria-expanded` on a button
 * called "Toggle" tells a screen-reader user that something opened without
 * saying what.
 */
function NavDisclosure({
  label,
  controls,
  open,
  onToggle,
}: {
  label: string;
  controls: string;
  open: boolean;
  onToggle: (button: HTMLElement) => void;
}) {
  return (
    <button
      type="button"
      className="psh-disclosure"
      aria-expanded={open}
      aria-controls={controls}
      aria-label={`${label} sections`}
      onClick={(e) => onToggle(e.currentTarget)}
    >
      {/* ⚠️ Drawn rather than an icon: it is one CSS-rotated chevron that follows
          `aria-expanded`, and the burger glyph beside it is drawn for the same
          reason. (Icons ARE available in the portal now — the KyckSocial pages
          import lucide-react — so this is a choice, not a missing dependency.)
          `aria-hidden` because the button's own name and `aria-expanded` already
          say everything. */}
      <span className="psh-disclosure-glyph" aria-hidden="true" />
    </button>
  );
}

/** A tooltip (a hovered or focused row on the folded rail) and a flyout (a group opened from it): both are fixed-position, outside the scrolling nav. */
type RailTip = { label: string; top: number; left: number };
type RailFlyout = { id: string; label: string; entries: FlyoutEntry[]; top: number; left: number };

export function PortalNav({
  selection,
  collapsed = false,
  onToggleCollapsed,
}: {
  selection?: PortalNavSelection | undefined;
  /**
   * The rail is folded to icons (≥1024px only: the CSS and the handlers below both gate on the width). ⚠️ The DOM is the SAME folded or
   * not (the labels are visually hidden, never removed, so every link keeps its accessible name); only the stylesheet and these handlers differ.
   */
  collapsed?: boolean;
  /** Absent = no collapse button (the nav is mounted on its own, as the nav tests do). */
  onToggleCollapsed?: () => void;
}) {
  const { pathname, search } = useLocation();
  const me = useQuery({ queryKey: ['portal-me'], queryFn: fetchPortalMe, retry: false });
  // ⚠️⚠️ Round 6: THIS VIEWER'S nav — industry picks the strategy children,
  // content decides Custom reports. There is no static list to fall back to.
  const viewerNav = usePortalViewerNav();
  const [open, setOpen] = useState(false);
  /**
   * Sections the reader has opened or closed BY HAND (§5, rule replaced §3).
   *
   * ⚠️⚠️ AN OVERRIDE MAP, NOT THE OPEN SET ITSELF, and the reason survived the
   * rule change: the default comes from `navSectionStartsOpen`, evaluated per
   * render, so a reader who collapses Reports keeps it collapsed while every
   * other section still answers to the current rule. Seeding an open set once
   * would freeze whatever the rule happened to be at mount.
   *
   * ⚠️ The default no longer depends on the route at all (§3): Reports is always
   * open, coming-soon always shut. So this map is now the ONLY thing that varies,
   * which is what a reader's own choice should be.
   */
  const [sectionOverride, setSectionOverride] = useState<Record<string, boolean>>({});
  const navRef = useRef<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [tip, setTip] = useState<RailTip | null>(null);
  const [flyout, setFlyout] = useState<RailFlyout | null>(null);
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const flyoutOrigin = useRef<HTMLElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    // ⚠️ FOCUS RETURNS TO THE BUTTON. Dismissing a panel that took focus and
    // leaving focus on a hidden element strands a keyboard reader at the top of
    // the document with no idea where they are.
    buttonRef.current?.focus();
  }, []);

  // ⚠️⚠️ IT CLOSES ON NAVIGATION, KEYED ON pathname *AND* SEARCH — THE HALF
  // THAT IS MOST OFTEN MISSED, and here it is not a subtlety: every club row is
  // the SAME PATH with a different `?account=`, so keying on pathname alone
  // would leave the panel open over exactly the report the client just asked
  // for. Location rather than an onClick per link, so the browser's own back
  // and forward close it too.
  useEffect(() => {
    setOpen(false);
    setFlyout(null);
    setTip(null);
  }, [pathname, search]);

  // A flyout closes on a press anywhere outside it. (A press on a section's own control is left to that control, which toggles.)
  useEffect(() => {
    if (!flyout) return;
    const onDown = (e: Event) => {
      const t = e.target as Element | null;
      if (!t || flyoutRef.current?.contains(t) || t.closest('.psh-disclosure')) return;
      setFlyout(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [flyout]);

  // ⚠️ ON OPEN, FOCUS THE FIRST *FOCUSABLE*, not the first child: fourteen of
  // the nav's ~20 rows are non-focusable `<span>`s (round 6 measured 5
  // focusables in the whole nav), so "focus the first item" lands on nothing.
  useEffect(() => {
    if (!open) return;
    navRef.current
      ?.querySelector<HTMLElement>('a[href], button:not(.psh-collapse), [tabindex]:not([tabindex="-1"])')
      ?.focus();
  }, [open]);

  const closeFlyout = () => {
    setFlyout(null);
    flyoutOrigin.current?.focus();
  };

  /** Fold a section's control: on the folded rail it opens (or closes) a flyout; everywhere else it opens or closes the sublist as before. */
  const toggleSection = (id: string, label: string, sectionOpen: boolean, entries: FlyoutEntry[], button: HTMLElement) => {
    if (collapsed && collapseApplies()) {
      if (flyout?.id === id) {
        closeFlyout();
        return;
      }
      const row = (button.closest('.psh-navrow') as HTMLElement | null) ?? button;
      flyoutOrigin.current = button;
      const at = flyoutPosition(row.getBoundingClientRect(), window.innerHeight, 0);
      setTip(null);
      setFlyout({ id, label, entries, ...at });
      return;
    }
    setSectionOverride((prev) => ({ ...prev, [id]: !sectionOpen }));
  };

  // The label of the hovered or focused row, as a tooltip beside the folded rail. ⚠️ Only on the folded rail and only at ≥1024px.
  const onTipTarget = (target: EventTarget | null) => {
    if (!collapsed || !collapseApplies() || flyout) return;
    const li = (target as Element | null)?.closest?.('[data-rail-label]') as HTMLElement | null;
    if (!li) {
      setTip(null);
      return;
    }
    const at = tipPosition(li.getBoundingClientRect());
    const label = li.dataset.railLabel ?? '';
    setTip((prev) => (prev && prev.label === label && prev.top === at.top ? prev : { label, ...at }));
  };

  // ⚠️ Nothing at all until the scope is known. A nav frame with no destinations
  // reads as a broken page; its absence for a moment reads as loading.
  if (!me.data || !viewerNav) return null;

  const reports = canSeeReports(me.data.permissions);
  // ⚠️ Round 6 §4: the Reports sub-list is THREE kinds of row now — All
  // locations, one per account, and Custom reports — each its own kind, so the
  // active state cannot mistake one for another.
  const reportRows = reports ? portalReportRows(portalReportLinks(me.data.accounts), viewerNav) : [];

  // ⚠️⚠️ THE RAIL AND THE NAV ARE TWO ELEMENTS, AND THAT IS THE WHOLE FIX.
  // `position: sticky` + `align-self: flex-start` makes the nav only as tall as
  // its content, so its `ink-deep` background painted only that far and the
  // column below it showed the page ground — visible in Scott's screenshot at
  // about y=490.
  //
  // ⚠️ IT CANNOT BE FIXED BY GIVING THE NAV A HEIGHT. A stretched or
  // 100%-height sticky element cannot stick at all — the same trap the last
  // brief's §1 warned about, arrived at from the other side. So the RAIL
  // stretches and carries the background; the NAV stays content-height and
  // sticks inside it.
  return (
    // ⚠️ Escape is caught on the RAIL rather than the panel, so it works whether
    // focus is inside the panel or still on the button that opened it.
    <div
      className="psh-rail"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && flyout) closeFlyout();
        else if (e.key === 'Escape' && open) close();
      }}
    >
      {/* ⚠️⚠️ IN THE DOM AT EVERY WIDTH, `display: none` above 900px — which
          removes it from the ACCESSIBILITY TREE too, so no `aria-expanded` is
          announced on a viewport where it means nothing. Rendering it
          conditionally on a measured width would need a resize listener and a
          first-paint guess. */}
      <button
        type="button"
        ref={buttonRef}
        className="psh-burger"
        aria-expanded={open}
        aria-controls="psh-nav"
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span aria-hidden="true" className="psh-burger-glyph" />
        Menu
      </button>
      {/* ⚠️ `scroll-subtle` because the NAV scrolls itself above 900px (see the
          sticky rule in index.css). CLAUDE.md's convention is that the utility
          goes on whatever element carries the overflow — the default OS
          scrollbar reads as heavy against `ink-deep`, which is why it exists. */}
      <nav
        id="psh-nav"
        ref={navRef}
        // ⚠️⚠️ A CLASS, NOT AN INLINE STYLE. See the header: an inline
        // `display: none` would beat the ≥900px rules and hide the DESKTOP
        // nav, silently, whenever this was closed — which is its initial state.
        className={`psh-nav scroll-subtle${open ? '' : ' psh-nav--closed'}`}
        aria-label="Portal sections"
        onMouseOver={(e: MouseEvent<HTMLElement>) => onTipTarget(e.target)}
        onFocus={(e: FocusEvent<HTMLElement>) => onTipTarget(e.target)}
        onMouseLeave={() => setTip(null)}
        onBlur={() => setTip(null)}>
        {/* THE COLLAPSE BUTTON, at the top of the nav (≥1024px only: the stylesheet hides it below). `aria-expanded` is the MENU's state. */}
        {onToggleCollapsed && (
          <div className="psh-collapse-row">
            <button
              type="button"
              className="psh-collapse"
              aria-label={collapseLabel(collapsed)}
              aria-expanded={!collapsed}
              aria-controls="psh-nav"
              onClick={onToggleCollapsed}
              data-testid="portal-nav-collapse"
            >
              {collapsed ? <ChevronsRight size={18} strokeWidth={1.5} aria-hidden="true" /> : <ChevronsLeft size={18} strokeWidth={1.5} aria-hidden="true" />}
            </button>
          </div>
        )}
        <ul className="psh-navlist">
          {/* ⚠️ HOME FIRST (Run J). Navigation, not a feature: it comes from
              `navigationNavItems` and is absent from `liveNavItems`, which the home
              page's own sentence is derived from. Active only on the intro page. */}
          {navigationNavItems(viewerNav.items).map((item) => {
            const on = isPortalHomeActive(pathname);
            return (
              <li key={item.id} data-rail-label={item.label}>
                <span className="psh-navrow">
                  <NavLink
                    to={item.path}
                    end
                    className={on ? 'psh-on' : undefined}
                    aria-current={on ? 'page' : undefined}
                  >
                    <NavIcon id={item.id} />
                    <span className="psh-label">{item.label}</span>
                  </NavLink>
                </span>
              </li>
            );
          })}
          {liveNavItems(viewerNav.items).map((item) => {
            // ⚠️⚠️ A LIVE SECTION HEADER (2026-09-23 — "Strategy decks"). It has NO
            // destination of its own (`path?: never`), so it cannot be handed to
            // `isPortalNavItemActive`; its CHILDREN are live links and each lights
            // on its own path. The label is a span with `psh-navhead`, which is
            // what keeps it from being styled as a coming-soon item.
            if (isLiveHeader(item)) {
              const subId = `psh-sub-${item.id}`;
              const sectionOpen = sectionOverride[item.id] ?? navSectionStartsOpen(item);
              const entries: FlyoutEntry[] = item.children.map((child) => ({
                key: child.id,
                label: child.label,
                href: child.path,
                active: isPortalNavItemActive(pathname, child.path),
              }));
              return (
                <li key={item.id} data-rail-label={item.label}>
                  <span className="psh-navrow">
                    <span id={`psh-head-${item.id}`} className="psh-navhead">
                      <NavIcon id={item.id} />
                      {item.label}
                    </span>
                    <NavDisclosure
                      label={item.label}
                      controls={subId}
                      open={flyout?.id === item.id || sectionOpen}
                      onToggle={(button) => toggleSection(item.id, item.label, sectionOpen, entries, button)}
                    />
                  </span>
                  {sectionOpen && (
                    <ul
                      className="psh-sublist"
                      id={subId}
                      role="group"
                      aria-labelledby={`psh-head-${item.id}`}
                    >
                      {item.children.map((child) => {
                        const on = isPortalNavItemActive(pathname, child.path);
                        return (
                          <li key={child.id}>
                            <NavLink
                              to={child.path}
                              className={on ? 'psh-sub-live psh-on' : 'psh-sub-live'}
                              aria-current={on ? 'page' : undefined}
                            >
                              {child.label}
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            }
            // ⚠️ Reports is hidden entirely without the permission, rather than
            // shown disabled: "not available on your plan" and "coming soon" are
            // different statements, and putting the first in the second's clothing
            // would tell a client to wait for something they cannot have.
            if (item.id === 'reports' && !reports) return null;
            const hasSub = item.id === 'reports' && reportRows.length > 0;
            const subId = `psh-sub-${item.id}`;
            const sectionOpen = sectionOverride[item.id] ?? navSectionStartsOpen(item);
            const entries: FlyoutEntry[] = hasSub
              ? reportRows.map((row) =>
                  row.kind === 'custom'
                    ? { key: portalReportRowKey(row), label: row.child.label, href: row.child.path, active: isPortalReportRowActive(row, selection, pathname) }
                    : { key: portalReportRowKey(row), label: row.link.name, href: row.link.href, active: isPortalReportRowActive(row, selection, pathname) },
                )
              : [];
            return (
              <li key={item.id} data-rail-label={item.label}>
                {/* ⚠️ THE ROW IS A FLEX PAIR: the destination and, where there are
                    sub-items, the control that reveals them. The link keeps its own
                    full-width pill; the disclosure sits beside it rather than
                    inside it, so clicking "Reports" still goes to All locations. */}
                <span className="psh-navrow">
                  <NavLink
                    to={item.path}
                    className={isPortalNavItemActive(pathname, item.path) ? 'psh-on' : undefined}
                  >
                    <NavIcon id={item.id} />
                    <span className="psh-label">{item.label}</span>
                  </NavLink>
                  {hasSub && (
                    <NavDisclosure
                      label={item.label}
                      controls={subId}
                      open={flyout?.id === item.id || sectionOpen}
                      onToggle={(button) => toggleSection(item.id, item.label, sectionOpen, entries, button)}
                    />
                  )}
                </span>
                {hasSub && sectionOpen && (
                  <ul className="psh-sublist" id={subId}>
                    {reportRows.map((row) => {
                      const on = isPortalReportRowActive(row, selection, pathname);
                      if (row.kind === 'custom') {
                        return (
                          <li key={portalReportRowKey(row)}>
                            {/* ⚠️ Round 6 §4: its OWN row kind, below the locations. */}
                            <NavLink
                              to={row.child.path}
                              className={on ? 'psh-sub-live psh-on' : 'psh-sub-live'}
                              aria-current={on ? 'page' : undefined}
                            >
                              {row.child.label}
                            </NavLink>
                          </li>
                        );
                      }
                      const loc = row.link;
                      return (
                      <li key={portalReportRowKey(row)}>
                        {/*
                          ⚠️⚠️ THE ACTIVE STATE COMES FROM THE RESOLVED SELECTION,
                          NOT FROM `NavLink`. `NavLink`'s own `isActive` compares
                          PATHS and every club row shares one, so it would light
                          them all at once — that part of the old reasoning was
                          right and is kept.

                          ⚠️⚠️ WHAT WAS WRONG WAS THE COMPENSATING CONTROL IT
                          NAMED: "the report's own header names the club it is
                          showing". It did not. The heading read the `accounts`
                          URL parameter, a nav drill-down writes `account`, and it
                          printed "All 6 locations" over one club. So the omission
                          rested on a surface that was never verified to do the
                          job — the fourth defect of that shape in this project,
                          after the 401-not-404 spec that proved only a router was
                          mounted and the `K_REVISION` stamp that prints a constant
                          in the only place it runs. AN OMISSION JUSTIFIED BY
                          POINTING AT ANOTHER SURFACE IS ONLY SAFE IF THAT SURFACE
                          IS VERIFIED TO DO THE JOB.

                          ⚠️ `subset` matches NO row, deliberately: highlighting
                          "All locations" for a two-of-three selection would state
                          something untrue, and no single club row is right either.
                        */}
                        <NavLink
                          to={loc.href}
                          className={on ? 'psh-sub-live psh-on' : 'psh-sub-live'}
                          aria-current={on ? 'page' : undefined}
                        >
                          {loc.name}
                        </NavLink>
                      </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        <div className="psh-navgroup">
          <p className="psh-navheading">{PORTAL_NAV_SOON_HEADING}</p>
          <ul className="psh-navlist">
            {soonNavItems(viewerNav.items).map((item) => {
              const hasSub = Boolean(item.children && item.children.length > 0);
              const subId = `psh-sub-${item.id}`;
              // ⚠️ A coming-soon section can never contain the active item, so its
              // default is always closed — structurally, since a `PortalNavChild` has
              // no `path` to be a destination.
              const sectionOpen = sectionOverride[item.id] ?? navSectionStartsOpen(item);
              return (
              <li key={item.id} data-rail-label={item.label}>
                <span className="psh-navrow">
                  <span id={`psh-soon-${item.id}`} aria-disabled="true">
                    <NavIcon id={item.id} />
                    {item.label}
                  </span>
                  {hasSub && (
                    <NavDisclosure
                      label={item.label}
                      controls={subId}
                      open={sectionOpen}
                      onToggle={() =>
                        setSectionOverride((prev) => ({ ...prev, [item.id]: !sectionOpen }))
                      }
                    />
                  )}
                </span>
                {/*
                  ⚠️⚠️ THE SUB-ITEMS ARE SPANS TOO, and for the same reason the
                  parents are: they must be genuinely non-interactive, not
                  clickable-then-disappointing and not in the tab order. The
                  model makes it structural — a `PortalNavChild` has no `path`
                  field at all, so there is nothing to render as an anchor even
                  by accident.

                  ⚠️⚠️ AND THE LIST IS ASSOCIATED WITH ITS PARENT. "Inspiration"
                  appears under BOTH Campaigns and Organic social, so a screen
                  reader meeting the second one has no way to tell which section
                  it belongs to. `aria-labelledby` points at the parent's own
                  span, so the group is announced by the name already on screen
                  rather than by a duplicated string that could drift from it.
                */}
                {hasSub && sectionOpen && (
                  <ul
                    className="psh-sublist"
                    id={subId}
                    role="group"
                    aria-labelledby={`psh-soon-${item.id}`}
                  >
                    {(item.children ?? []).map((child) => (
                      <li key={child.id}>
                        {/* ⚠️ `psh-sub-soon` vs the live children's `psh-sub-live`: the two
                            kinds must never end up with the same classes (asserted in
                            nav.render.test.tsx). Muting is what says "not available yet". */}
                        <span className="psh-sub-soon" aria-disabled="true">{child.label}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
              );
            })}
          </ul>
        </div>
      </nav>
      {/* ⚠️ FIXED-POSITION AND OUTSIDE THE NAV: the nav scrolls itself (overflow), which would clip anything absolutely positioned inside it. The
          rail declares no overflow (sticky needs that), so these are not clipped here. A tooltip is aria-hidden: the row's own accessible name
          already says it. */}
      {tip && !flyout && (
        <div className="psh-tip" aria-hidden="true" style={{ top: tip.top, left: tip.left }} data-testid="portal-nav-tip">
          {tip.label}
        </div>
      )}
      {flyout && (
        <div
          ref={flyoutRef}
          className="psh-flyout"
          role="group"
          aria-label={`${flyout.label} sections`}
          style={{ top: flyout.top, left: flyout.left }}
          data-testid="portal-nav-flyout"
        >
          <p className="psh-flyout-title">{flyout.label}</p>
          <ul className="psh-sublist">
            {flyout.entries.map((entry) => (
              <li key={entry.key}>
                {entry.href ? (
                  <NavLink to={entry.href} className={entry.active ? 'psh-sub-live psh-on' : 'psh-sub-live'} aria-current={entry.active ? 'page' : undefined}>
                    {entry.label}
                  </NavLink>
                ) : (
                  <span className="psh-sub-soon" aria-disabled="true">{entry.label}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
